import {
  ChangeEventHandler,
  MouseEventHandler,
  useEffect,
  useState,
} from "react";
import { OrgUserRole, Profile, ProjectUserRole } from "../common/ObjectTypes";
import SideMenu from "../common/SideMenu";
import TextInput from "../common/TextInput";
import SelectInput from "../common/SelectInput";
import { ActionTypes } from "../common/Enums";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { reinviteUser, updateUser } from "../../api/usersApi";
import ConfirmationModal from "../common/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import { Delete } from "../common/ProjectIcons";
import tailwindClassnames from "../common/TailwindClassnames";
import RolePermissionsInfo from "../common/RolePermissionsInfo";
import { BeatLoader } from "react-spinners";

type UserDetailsProps = {
  user: Profile;
  isMyAccount: boolean;
  roleLoading: boolean;
  existingUser: boolean;
  onChange: ChangeEventHandler;
  onCancel: MouseEventHandler;
  onSave: MouseEventHandler;
  onRemoveOrgRole: Function;
  onRemoveProjectRole: Function;
  errors: any;
  newAccount: boolean;
  profile: any;
  isSaving: boolean;
  saveDisabled: boolean;
  onSelectOrganization: (org_id: number) => void;
  onSelectProject: (project_id: number) => void;
};

const UserDetails = ({
  user,
  isMyAccount,
  existingUser,
  roleLoading,
  onChange,
  onCancel,
  onSave,
  onRemoveOrgRole,
  onRemoveProjectRole,
  errors,
  newAccount,
  profile,
  isSaving,
  saveDisabled,
  onSelectOrganization,
  onSelectProject,
}: UserDetailsProps) => {
  const [actions, setActions] = useState<any[]>([]);
  const { t } = useTranslation();
  const [activateConfirmationModalIsOpen, setActivateConfirmationModalIsOpen] =
    useState<boolean>(false);
  const [
    deactivateConfirmationModalIsOpen,
    setDeactivateConfirmationModalIsOpen,
  ] = useState<boolean>(false);
  const [
    removeRoleConfirmationModalIsOpen,
    setRemoveRoleConfirmationModalIsOpen,
  ] = useState<boolean>(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [confirmAction, setConfirmAction] = useState<Function>(() => () => {});
  const [selectedEntityName, setSelectedEntityName] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    let tempActions = [];
    if (!user.is_verified) {
      tempActions.push({
        label: t("Resend Invitation"),
        value: ActionTypes.RESEND_INVITATION,
      });
      tempActions.push({
        label: t("Remove Invitation"),
        value: ActionTypes.REMOVE_INVITATION,
      });
    }
    if (user.is_active) {
      tempActions.push({
        label: t("Deactivate Account"),
        value: ActionTypes.DEACTIVATE_USER,
      });
    } else {
      tempActions.push({
        label: t("Activate Account"),
        value: ActionTypes.ACTIVATE_USER,
      });
    }
    setActions(tempActions);
  }, []);

  const handleAction = (action: any) => {
    if (action === null) {
      return;
    }
    setSelectedAction(action.value);
  };

  useEffect(() => {
    if (selectedAction !== null) {
      handleActionSend(selectedAction);
    }
  }, [selectedAction]);

  const handleActionSend = async (action: any) => {
    if (selectedAction !== null) {
      if (selectedAction === ActionTypes.RESEND_INVITATION) {
        try {
          await reinviteUser(user.user_id);
          toast.success(t("Invitation Resent"));
        } catch (err) {
          toast.error(t("Error Resending Invitation"));
        }
      } else if (selectedAction === ActionTypes.DEACTIVATE_USER) {
        setDeactivateConfirmationModalIsOpen(true);
      } else if (selectedAction === ActionTypes.ACTIVATE_USER) {
        setActivateConfirmationModalIsOpen(true);
      }
    }
  };

  const handleActivateUser = async () => {
    try {
      await updateUser(user.user_id, {
        is_active: true,
      });
      toast.success(t("User Activated"));
      setActivateConfirmationModalIsOpen(false);
      navigate("/users");
    } catch (err) {
      toast.error(t("Error Activating User"));
    }
  };

  const handleDeactivateUser = async () => {
    try {
      await updateUser(user.user_id, {
        is_active: false,
      });
      toast.success(t("User Deactivated"));
      setDeactivateConfirmationModalIsOpen(false);
      navigate("/users");
    } catch (err) {
      toast.error(t("Error Deactivating User"));
    }
  };

  const handleOpenRemoveRoleModal = (action: any) => {
    setConfirmAction(() => action); // Store the action to be confirmed
    setRemoveRoleConfirmationModalIsOpen(true);
  };

  const handleCloseRemoveRoleModal = () => {
    setRemoveRoleConfirmationModalIsOpen(false);
    setConfirmAction(() => () => {});
    setSelectedEntityName("");
  };

  const handleConfirmRemoveRole = () => {
    if (confirmAction) {
      confirmAction(); // Execute the stored action
    }
    handleCloseRemoveRoleModal();
  };

  const handleRemoveProjectRole = (e: any, role: any): void => {
    e.stopPropagation();
    setSelectedEntityName(role.project_name);
    handleOpenRemoveRoleModal(() => onRemoveProjectRole(role));
  };

  const handleRemoveOrgRole = (e: any, role: any): void => {
    e.stopPropagation();
    setSelectedEntityName(role.org_name);
    handleOpenRemoveRoleModal(() => onRemoveOrgRole(role));
  };

  return (
    <div className="flex flex-row w-full">
      <div>
        <SideMenu />
      </div>
      <div className="overflow-x-auto w-3/4 mx-8 mt-8">
        <div className={tailwindClassnames.pageHeaderRow}>
          <div className="flex flex-row w-full items-center">
            <h1 className={tailwindClassnames.pageTitle}>
              {existingUser
                ? isMyAccount
                  ? t("My Account")
                  : t("User Details")
                : t("Add User")}
            </h1>
            {existingUser && !user.is_verified && (
              <label className={`${tailwindClassnames.warningLabel} ml-4`}>
                {t("Account is not verified.")}
              </label>
            )}
          </div>
          {existingUser && !newAccount && !isMyAccount && profile.is_admin && (
            <div className="h-full w-full flex justify-end pr-1">
              <div className="w-1/3">
                <SelectInput
                  className={
                    "w-full rounded shadow text-off-black-light text-sm"
                  }
                  defaultValue={undefined}
                  isDisabled={false}
                  isLoading={false}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  name={t("Actions")}
                  errors={""}
                  options={actions}
                  onChange={handleAction}
                  searchBar={true}
                />
              </div>
              <ConfirmationModal
                isOpen={activateConfirmationModalIsOpen}
                onConfirm={() => {
                  handleActivateUser();
                }}
                onCancel={() => {
                  setActivateConfirmationModalIsOpen(false);
                }}
                confirmationMessage={t(
                  "Are you sure you want to activate this account?",
                )}
              />
              <ConfirmationModal
                isOpen={deactivateConfirmationModalIsOpen}
                onConfirm={() => {
                  handleDeactivateUser();
                }}
                onCancel={() => {
                  setDeactivateConfirmationModalIsOpen(false);
                }}
                confirmationMessage={t(
                  "Are you sure you want to deactivate this account?",
                )}
                danger={true}
              />
            </div>
          )}
        </div>
        <section className={tailwindClassnames.formSectionCard}>
          {existingUser && !user.is_active && (
            <p className={tailwindClassnames.warningLabel}>
              {t("Account is Inactive")}
            </p>
          )}
          <div className="flex flex-row w-full mb-2">
            <div
              className={`${tailwindClassnames.detailsPageTextInputContainer} pl-0`}
            >
              <TextInput
                name="first_name"
                label={t("First Name") + "*"}
                placeholder=""
                value={user ? user.first_name : ""}
                error={errors.first_name}
                disabled={false}
                className={tailwindClassnames.detailsPageTextInput}
                type="text"
                onChange={(e: any) => onChange(e)}
              />
            </div>
            <div
              className={`${tailwindClassnames.detailsPageTextInputContainer} pr-0`}
            >
              <TextInput
                name="last_name"
                label={t("Last Name") + "*"}
                error={errors.last_name}
                disabled={false}
                type="text"
                placeholder=""
                value={user ? user.last_name : ""}
                className={tailwindClassnames.detailsPageTextInput}
                onChange={(e: any) => onChange(e)}
              />
            </div>
          </div>
          <div className="flex flex-col items-start w-full">
            {existingUser ? (
              <p className={tailwindClassnames.basicLabel}>
                {t("Email")}: {user ? user.email : ""}
              </p>
            ) : (
              <div className="flex flex-row w-full mb-3 -ml-3">
                <div
                  className={tailwindClassnames.detailsPageTextInputContainer}
                >
                  <TextInput
                    name="email"
                    label={t("Email") + "*"}
                    error={errors.email}
                    disabled={false}
                    type="text"
                    placeholder=""
                    value={user ? user.email : ""}
                    className={tailwindClassnames.detailsPageTextInput}
                    onChange={(e: any) => onChange(e)}
                  />
                </div>
              </div>
            )}
            <div className="flex flex-row w-full mb-2 ">
              <div
                className={`${tailwindClassnames.detailsPageTextInputContainer} pl-0`}
              >
                <TextInput
                  name="organization"
                  label={t("Organization")}
                  error={errors.organization}
                  disabled={false}
                  type="text"
                  placeholder=""
                  value={user ? user.organization : ""}
                  className={tailwindClassnames.detailsPageTextInput}
                  onChange={(e: any) => onChange(e)}
                />
              </div>
              <div
                className={`${tailwindClassnames.detailsPageTextInputContainer} pr-0`}
              >
                <TextInput
                  name="job_title"
                  label={t("Job Title")}
                  error={errors.job_title}
                  disabled={false}
                  type="text"
                  placeholder=""
                  value={user ? user.job_title : ""}
                  className={tailwindClassnames.detailsPageTextInput}
                  onChange={(e: any) => onChange(e)}
                />
              </div>
            </div>

            {profile.is_admin && (
              <div className="flex flex-row items-start">
                <input
                  className={tailwindClassnames.checkBox}
                  type="checkbox"
                  value=""
                  id="is_admin"
                  checked={user.is_admin}
                  onChange={(e: any) => onChange(e)}
                />
                <div className="flex flex-col">
                  <label className={tailwindClassnames.checkBoxLabel}>
                    {t("System Admin")}
                  </label>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-row-reverse gap-3">
            <button
              type="button"
              className={`relative ${tailwindClassnames.btnPrimary}`}
              onClick={onSave}
              disabled={saveDisabled || isSaving}
            >
              {/* Label stays in layout while saving so the button keeps its
                  width; currentColor spinner reads on the fill in both
                  themes. */}
              <span className={isSaving ? "invisible" : ""}>{t("Save")}</span>
              {isSaving && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <BeatLoader
                    color="currentColor"
                    loading={true}
                    size={10}
                    margin={5}
                  />
                </span>
              )}
            </button>
            <button
              type="button"
              className={tailwindClassnames.btnSecondary}
              onClick={onCancel}
            >
              {t("Cancel")}
            </button>
          </div>
        </section>
        {existingUser && !newAccount && (
          <div>
            <ConfirmationModal
              isOpen={removeRoleConfirmationModalIsOpen}
              onConfirm={handleConfirmRemoveRole}
              onCancel={handleCloseRemoveRoleModal}
              confirmationMessage={
                t("Are you sure you want to remove the user") +
                ` ${user.first_name} ${user.last_name} ` +
                t("from") +
                ` ${selectedEntityName}?`
              }
              danger={true}
            />
            <div className={`${tailwindClassnames.tableCard} mt-3`}>
              <table className="w-full">
                <thead>
                  <tr>
                    <th
                      className={`${tailwindClassnames.tableCardHeadCell} w-1/3`}
                    >
                      {t("Organizations")}
                    </th>
                    <th
                      className={`${tailwindClassnames.tableCardHeadCell} w-1/3`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {t("Role")}
                        <RolePermissionsInfo scope="organization" />
                      </span>
                    </th>
                    <th className={tailwindClassnames.tableCardHeadCell}></th>
                  </tr>
                </thead>
                <tbody>
                  {user.org_roles.length > 0 ? (
                    user.org_roles.map((role: OrgUserRole) => {
                      const roleLabel = t(role.role_name);
                      return (
                        <tr
                          key={`${role.org_id}-${role.role_name}`}
                          className={tailwindClassnames.tableCardRow}
                          onClick={() => onSelectOrganization(role.org_id)}
                        >
                          <td
                            className={tailwindClassnames.tableCardCellPrimary}
                            title={role.org_name ?? undefined}
                          >
                            {role.org_name}
                          </td>
                          <td
                            className={tailwindClassnames.tableCardCell}
                            title={roleLabel || undefined}
                          >
                            {roleLabel.charAt(0).toUpperCase() +
                              roleLabel.slice(1).toLowerCase()}
                          </td>
                          <td
                            className={tailwindClassnames.tableCardCell}
                            onClick={(e) => handleRemoveOrgRole(e, role)}
                          >
                            <Delete />
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr className={tailwindClassnames.tableCardRowStatic}>
                      <td className={tailwindClassnames.tableCardCell}>
                        {t("No organizations")}
                      </td>
                      <td className={tailwindClassnames.tableCardCell}></td>
                      <td className={tailwindClassnames.tableCardCell}></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <br />
            <div className={`${tailwindClassnames.tableCard} mt-3`}>
              <table className="w-full">
                <thead>
                  <tr>
                    <th
                      className={`${tailwindClassnames.tableCardHeadCell} w-1/3`}
                    >
                      {t("Projects")}
                    </th>
                    <th
                      className={`${tailwindClassnames.tableCardHeadCell} w-1/3`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {t("Role")}
                        <RolePermissionsInfo scope="project" />
                      </span>
                    </th>
                    <th className={tailwindClassnames.tableCardHeadCell}></th>
                  </tr>
                </thead>
                <tbody>
                  {user.project_roles.length > 0 ? (
                    user.project_roles.map(
                      (role: ProjectUserRole, index: any) => {
                        const roleLabel = t(role.role_name);
                        return (
                          <tr
                            key={role.project_id}
                            className={tailwindClassnames.tableCardRow}
                            onClick={() => onSelectProject(role.project_id)}
                          >
                            <td
                              className={
                                tailwindClassnames.tableCardCellPrimary
                              }
                              title={role.project_name ?? undefined}
                            >
                              {role.project_name}
                            </td>
                            <td
                              className={tailwindClassnames.tableCardCell}
                              title={roleLabel || undefined}
                            >
                              {roleLabel.charAt(0).toUpperCase() +
                                roleLabel.slice(1).toLowerCase()}
                            </td>
                            <td
                              className={tailwindClassnames.tableCardCell}
                              onClick={(e) => handleRemoveProjectRole(e, role)}
                            >
                              <Delete />
                            </td>
                          </tr>
                        );
                      },
                    )
                  ) : (
                    <tr className={tailwindClassnames.tableCardRowStatic}>
                      <td className={tailwindClassnames.tableCardCell}>
                        {t("No projects")}
                      </td>
                      <td className={tailwindClassnames.tableCardCell}></td>
                      <td className={tailwindClassnames.tableCardCell}></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetails;
